import {
  NavigationError,
  type NavigationErrorHandlerFeature,
  withNavigationErrorHandler,
} from '@angular/router';
import { isChunkLoadError } from './lazy-chunk';

/**
 * The recovery `lazyChunk` cannot perform for itself: a new document.
 *
 * `lazyChunk` buys a dropped chunk back by re-importing it under a cache-
 * busted URL, which works as long as the request that dropped was the chunk
 * the router asked for by name. When it was one of that chunk's *static*
 * dependencies, there is no specifier to bust - the browser only ever names
 * the dynamically imported parent - and the module map holds the failure for
 * the life of the document. See lazy-chunk.ts for why.
 *
 * A document that starts with an empty module map is then the only way out, so
 * a navigation that ran out of attempts is finished as a hard load of the URL
 * the visitor asked for. They land where they were going, having paid a full
 * page load; the alternative is a link that does nothing until they reload by
 * hand, which is the bug this pair of files exists to fix.
 *
 * Only navigations are recovered here. A modal or any other caller of
 * `lazyChunk` gets the `ChunkLoadError` and decides for itself - throwing a
 * visitor's unsaved map state away to open a dialog is not this file's call.
 */

/** Marks the last hard load this tab performed to recover a chunk. */
const RELOAD_MARK_KEY = 'lazyChunkReload';

/**
 * How long a mark counts as "we just tried that".
 *
 * A reload only helps when the chunk exists and the request for it dropped. If
 * the chunk is simply gone - a tab held open across a deploy, so index.html
 * names hashes the server no longer has - the fresh document fails the same
 * way, and without this it would reload forever. Long enough to cover that
 * round trip, short enough that an unrelated failure an hour later is still
 * recovered.
 */
const RELOAD_GUARD_MS = 60_000;

interface ReloadMark {
  url: string;
  at: number;
}

export function withChunkRecovery(): NavigationErrorHandlerFeature {
  return withNavigationErrorHandler((event: NavigationError) => {
    if (!isChunkLoadError(event.error)) return;
    recoverNavigation(event.url, event.error);
  });
}

function recoverNavigation(routerUrl: string, error: unknown): void {
  if (typeof window === 'undefined') return;

  // `location.assign` leaves the app, so the target is checked the same way
  // lazy-chunk.ts checks the URL it retries. A router URL should always be a
  // path on this origin, but a protocol-relative one would navigate off-site,
  // and a failed navigation is not a good moment to find that out.
  const url = sameOriginTarget(routerUrl);
  if (!url) {
    console.error(
      `[lazy-chunk] refusing to reload for the off-origin URL ${routerUrl}`,
      error
    );
    return;
  }

  if (reloadedRecentlyFor(url)) {
    // Reloading again would only spin. This is the one outcome worth an issue:
    // the chunk is missing rather than dropped, which is a deploy problem.
    console.error(
      `[lazy-chunk] ${url} is still unreachable after a reload`,
      error
    );
    return;
  }

  // Logged before leaving so the session carries a reason for the reload.
  console.error(`[lazy-chunk] reloading the document to reach ${url}`, error);
  markReloadFor(url);
  window.location.assign(url);
}

function sameOriginTarget(url: string): string | null {
  try {
    const target = new URL(url, window.location.origin);
    return target.origin === window.location.origin ? target.href : null;
  } catch {
    return null;
  }
}

function reloadedRecentlyFor(url: string): boolean {
  const mark = readMark();
  return (
    mark !== null && mark.url === url && Date.now() - mark.at < RELOAD_GUARD_MS
  );
}

function readMark(): ReloadMark | null {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_MARK_KEY);
    if (!raw) return null;
    const mark = JSON.parse(raw) as Partial<ReloadMark>;
    if (typeof mark?.url !== 'string' || typeof mark?.at !== 'number') {
      return null;
    }
    return { url: mark.url, at: mark.at };
  } catch {
    // Unavailable (Safari private browsing throws on access) or unparseable.
    // Losing the mark costs at most one extra reload, so it is not worth
    // reporting - but it must not take the navigation down with it.
    return null;
  }
}

function markReloadFor(url: string): void {
  try {
    const mark: ReloadMark = { url, at: Date.now() };
    window.sessionStorage.setItem(RELOAD_MARK_KEY, JSON.stringify(mark));
  } catch {
    // See readMark.
  }
}
